import { useEffect, useMemo, useCallback, useRef } from "react";

import {
  Annotation,
  AnnotationSource,
  isClipAnnotation,
  isCollectionAnnotation,
  isTimeBoxAnnotation,
  isTimedPointAnnotation,
  isTimedPolygonAnnotation,
  isTrackAnnotation,
} from "@perceptron/core/types";

import { useDisplayedMediaIndex, useMediaCarouselStore } from "@demo/store/media-carousel";
import { useModelStore } from "@demo/store/model";
import { useToolCallStore } from "@demo/store/tool-calls";
import { isZoomBox } from "@demo/utils/annotations";

import { annotationIdsIn, annotationIndex, mergeAnnotationSets, sourceScopedAnnotations } from "./annotationIndex";
import { annotationsForAsset } from "./assetPartition";
import { abandonAssetPeek, beginAssetPeek, commitAssetPeek, endAssetPeek } from "./assetPeek";

/** Recurses through collections; tracks are atomic and not descended into. */
const flattenAnnotation = (annotation: Annotation): Annotation[] => {
  if (isCollectionAnnotation(annotation)) {
    return [annotation, ...annotation.children.flatMap(flattenAnnotation)];
  }
  return [annotation];
};

/** Retain a selected collection once, without duplicating its selected descendants. */
const selectAnnotationRoots = (annotation: Annotation, ids: ReadonlySet<string>): Annotation[] => {
  if (ids.has(annotation.id)) return [annotation];
  return isCollectionAnnotation(annotation)
    ? annotation.children.flatMap((child) => selectAnnotationRoots(child, ids))
    : [];
};

const isTimedAnnotation = (annotation: Annotation | undefined): boolean =>
  !!annotation &&
  (isClipAnnotation(annotation) ||
    isTimeBoxAnnotation(annotation) ||
    isTimedPointAnnotation(annotation) ||
    isTimedPolygonAnnotation(annotation) ||
    isTrackAnnotation(annotation));

/** Timed annotations navigate video; they do not necessarily support track focus. */
export function isVideoAnnotation(annotation: Annotation | undefined): boolean {
  return (
    isTimedAnnotation(annotation) ||
    (!!annotation && isCollectionAnnotation(annotation) && annotation.children.some(isVideoAnnotation))
  );
}

/** Only tracks and their ancestor collections participate in video focus. */
export function isVideoFocusAnnotation(annotation: Annotation | undefined): boolean {
  return (
    !!annotation &&
    (isTrackAnnotation(annotation) ||
      (isCollectionAnnotation(annotation) && annotation.children.some(isVideoFocusAnnotation)))
  );
}

/** Player locks retain tracks and collections containing tracks during playback. */
export function isPinnedVideoAnnotation(annotation: Annotation | undefined): boolean {
  return isVideoFocusAnnotation(annotation);
}

const supportsFocus = (annotation: Annotation | undefined): boolean =>
  !isVideoAnnotation(annotation) || isVideoFocusAnnotation(annotation);

export interface AnnotationInteractions {
  highlightedAnnotationIds?: string[];
  clickedAnnotationId?: string;
  setHighlightedAnnotationIds: (ids?: string[], source?: AnnotationSource) => void;
  setClickedAnnotationId: (id?: string, source?: AnnotationSource) => void;
  isHighlighted: (id: string) => boolean;
  isDimmed: (id: string) => boolean;
  isCollectionHighlighted: (ids: string[]) => boolean;
  /**
   * Matches any highlighted id that equals a given key or is a descendant
   * (prefixed with `${key}::`, e.g. track cluster ids). Accepts a single key
   * or an array.
   */
  isKeyHighlighted: (keyOrKeys: string | string[]) => boolean;
  /** Scope-aware equivalent of `clickedAnnotationId === key`. */
  isKeyClicked: (keyOrKeys: string | string[]) => boolean;
  handleMouseEnter: (ids: string[], source: AnnotationSource) => void;
  handleMouseLeave: (id: string) => void;
  handleClick: (id: string, source: AnnotationSource) => void;
  allAnnotations: Annotation[];
  allFlattenedAnnotations: Annotation[];
  findAnnotationById: (id: string) => Annotation | undefined;
  visibleAnnotations: Annotation[];
  visibleAnnotationIds: Set<string>;
}

export const useAnnotationInteractions = (): AnnotationInteractions => {
  const setHighlightedAnnotationIds = useModelStore((state) => state.setHighlightedAnnotationIds);
  const setClickedAnnotationId = useModelStore((state) => state.setClickedAnnotationId);
  const highlightedAnnotationIds = useModelStore((state) => state.highlightedAnnotationIds);
  const highlightedAnnotationSource = useModelStore((state) => state.highlightedAnnotationSource);
  const clickedAnnotationId = useModelStore((state) => state.clickedAnnotationId);
  const clickedAnnotationSource = useModelStore((state) => state.clickedAnnotationSource);
  const annotations = useModelStore((state) => state.annotations);
  // Two different asset indices, and the distinction is load-bearing:
  // `displayed` is what is on screen, preview included; `committed` is what the
  // carousel returns to when no preview is in play. Hover and click decisions
  // are judged against `committed` — against `displayed` they read as "already
  // there" mid-preview and cancel the very preview that put it there.
  const displayedAssetIdx = useDisplayedMediaIndex();
  const committedAssetIdx = useMediaCarouselStore((state) => state.currentMediaIndexInAgentCarousel);
  const selectAssetManually = useMediaCarouselStore((state) => state.selectMediaManually);
  const setPeekAsset = useMediaCarouselStore((state) => state.setPeekMediaIndex);
  const reasoningAnnotations = useModelStore((state) => state.reasoningAnnotations);
  const zoomAnnotations = useToolCallStore((state) => state.zoomAnnotations);

  // The regions the model zoomed into come from the tool-call events, not the parsed text; they join the
  // content set so a zoom card can select its region on the image like any content annotation.
  const contentAnnotations = mergeAnnotationSets(annotations, zoomAnnotations) as Annotation[];

  // Derived once per response and shared by every component, rather than
  // rebuilt inside each instance's `useMemo` (see ./annotationIndex).
  const allAnnotations = mergeAnnotationSets(reasoningAnnotations, contentAnnotations) as Annotation[];
  const index = annotationIndex(allAnnotations);
  const allFlattenedAnnotations = index.flattened as Annotation[];

  const findAnnotationById = useCallback((id: string) => index.byId.get(id), [index]);

  const pinnedVideoAnnotation = useMemo(() => {
    const annotation = clickedAnnotationId ? findAnnotationById(clickedAnnotationId) : undefined;
    return isPinnedVideoAnnotation(annotation) ? annotation : undefined;
  }, [clickedAnnotationId, findAnnotationById]);

  // Collections have their own identity, not a prefix shared by their children.
  // Resolve their current descendants so streamed tracks join the same focus.
  const expandVideoScopes = (ids: readonly string[]): string[] =>
    ids.flatMap((id) => {
      const annotation = findAnnotationById(id);
      if (!supportsFocus(annotation)) return [];
      return annotation && isCollectionAnnotation(annotation) && isVideoFocusAnnotation(annotation)
        ? flattenAnnotation(annotation)
            .filter(isVideoFocusAnnotation)
            .map((node) => node.id)
        : [id];
    });
  const highlightedKeys = expandVideoScopes(
    highlightedAnnotationIds?.length
      ? highlightedAnnotationIds
      : pinnedVideoAnnotation
        ? [pinnedVideoAnnotation.id]
        : [],
  );
  const clickedKeys = expandVideoScopes(clickedAnnotationId ? [clickedAnnotationId] : []);

  const scoped = sourceScopedAnnotations({
    annotations: contentAnnotations,
    reasoningAnnotations,
    flattened: allFlattenedAnnotations,
    clickedAnnotationId,
    clickedAnnotationSource,
    highlightedAnnotationIds,
    highlightedAnnotationSource,
    pinnedVideoAnnotation,
    selectRoots: selectAnnotationRoots,
  });

  // Cached on the identity of the annotations it describes, so switching asset
  // is a lookup and each slice keeps its identity for React to skip work on.
  const visibleAnnotations = annotationsForAsset(scoped, displayedAssetIdx) as Annotation[];

  // Flat id set for mask visibility checks.
  const visibleAnnotationIds = annotationIdsIn(visibleAnnotations) as Set<string>;

  const isHighlighted = (id: string) => {
    const isClicked = clickedAnnotationId === id;
    const isHighlighted = highlightedAnnotationIds?.includes(id);
    const isSinglePointHighlighted = highlightedAnnotationIds?.length === 1 && highlightedAnnotationIds[0] === id;
    return ((isClicked || isHighlighted) && isSinglePointHighlighted) ?? false;
  };

  const isCollectionHighlighted = (ids: string[]) => {
    const isClicked = clickedAnnotationId && ids.includes(clickedAnnotationId);
    const isHighlighted = highlightedAnnotationIds?.some((id) => ids.includes(id));
    const isCollectionHighlighted = highlightedAnnotationIds?.length && highlightedAnnotationIds.length >= ids.length;
    return ((isClicked || isHighlighted) && !!isCollectionHighlighted) ?? false;
  };

  const isDimmed = (id: string) =>
    (!!highlightedAnnotationIds?.length || clickedAnnotationId !== undefined) &&
    !isHighlighted(id) &&
    !!highlightedAnnotationIds?.length;

  // Encapsulates the `${trackKey}::c::${idx}` descendant-id convention so
  // consumers don't need to know the cluster-id format.
  const matchesInScope = (id: string, keys: string[]): boolean => {
    for (const key of keys) {
      if (id === key) return true;
      if (id.startsWith(`${key}::`)) return true;
    }
    return false;
  };

  const toKeyArr = (keyOrKeys: string | string[]): string[] =>
    typeof keyOrKeys === "string" ? [keyOrKeys] : keyOrKeys;

  const isKeyHighlighted = (keyOrKeys: string | string[]): boolean => {
    if (!highlightedKeys.length) return false;
    const keys = toKeyArr(keyOrKeys);
    if (keys.length === 0) return false;
    return highlightedKeys.some((id) => matchesInScope(id, keys));
  };

  const isKeyClicked = (keyOrKeys: string | string[]): boolean => {
    if (!clickedAnnotationId) return false;
    const keys = toKeyArr(keyOrKeys);
    if (keys.length === 0) return false;
    return clickedKeys.some((id) => matchesInScope(id, keys));
  };

  // Whether the selected annotation is a region the model zoomed into.
  const isZoomClicked = useMemo(
    () => !!clickedAnnotationId && isZoomBox(findAnnotationById(clickedAnnotationId)),
    [clickedAnnotationId, findAnnotationById],
  );

  // Identity for this annotation's pending preview. A chip that unmounts while
  // hovered never fires `mouseleave`, so its timer has to be dropped here.
  const peekOwner = useRef<symbol>(Symbol("asset-peek")).current;
  useEffect(() => () => abandonAssetPeek(peekOwner), [peekOwner]);

  const handleMouseEnter = (ids: string[], source: AnnotationSource) => {
    // Preview the asset the hovered annotation lives on.
    const hovered = ids.map(findAnnotationById).find((a) => a !== undefined);
    if (hovered) beginAssetPeek(hovered.assetIdx, committedAssetIdx, setPeekAsset, peekOwner);

    // A selected image zoom keeps its hover lock; video focus is transient.
    if (isZoomClicked && !pinnedVideoAnnotation) return;
    const focusIds = ids.filter((id) => supportsFocus(findAnnotationById(id)));
    if (focusIds.length) setHighlightedAnnotationIds(focusIds, source);
  };

  const handleMouseLeave = (id: string) => {
    endAssetPeek(setPeekAsset);
    if (!supportsFocus(findAnnotationById(id))) return;
    if (pinnedVideoAnnotation || clickedAnnotationId !== id) {
      setHighlightedAnnotationIds(undefined, undefined);
    }
  };

  const handleClick = (id: string, source: AnnotationSource) => {
    const annotation = findAnnotationById(id);

    // Activating an annotation on another asset brings that asset up first, or
    // the selection lands on something the viewer can't see. Before the early
    // returns, so it applies to tracks and clips too.
    commitAssetPeek();
    if (annotation && annotation.assetIdx !== committedAssetIdx) {
      selectAssetManually(annotation.assetIdx);
    }

    // Collection text expands/collapses; only the player's lock pins a collection.
    if (annotation && isCollectionAnnotation(annotation) && isVideoAnnotation(annotation)) return;
    if (isPinnedVideoAnnotation(annotation)) {
      const selected = clickedAnnotationId === id;
      setHighlightedAnnotationIds(undefined, undefined);
      setClickedAnnotationId(selected ? undefined : id, selected ? undefined : source);
      return;
    }
    if (isTimedAnnotation(annotation)) {
      // Fire-and-forget: briefly set so VideoOutput's effect observes and
      // seeks, then clear so the chip doesn't linger selected.
      setClickedAnnotationId(id, source);
      setTimeout(() => {
        // A subsequent track click must not be cleared by a prior seek-only click.
        if (useModelStore.getState().clickedAnnotationId === id) {
          setClickedAnnotationId(undefined, undefined);
        }
      }, 0);
      return;
    }

    // Persistent toggle — image-context selection (focus box zoom, etc.).
    setHighlightedAnnotationIds([id], source);
    if (clickedAnnotationId === id) {
      setClickedAnnotationId(undefined, undefined);
    } else {
      setClickedAnnotationId(id, source);
    }
  };

  return {
    highlightedAnnotationIds,
    clickedAnnotationId,
    setHighlightedAnnotationIds,
    setClickedAnnotationId,
    isHighlighted,
    isDimmed,
    isCollectionHighlighted,
    isKeyHighlighted,
    isKeyClicked,
    handleMouseEnter,
    handleMouseLeave,
    handleClick,
    allAnnotations,
    allFlattenedAnnotations,
    findAnnotationById,
    visibleAnnotations,
    visibleAnnotationIds,
  };
};
